import React, { ChangeEvent, useState } from 'react';
import styles from './NewCriterion.module.scss';
import { Checkbox } from '@tcc-online/input-components';
import { useTranslate } from 'react-translate.ts';
import { BooleanSchema, NumberSchema, ObjectSchema, StringSchema } from 'yup';
import useDebounce from '../../../../helpers/useDebounce';
import CriterionDisplayOption from '../../../types/criteria/CriterionDisplayOption';
import AdminInput from '../../input/AdminInput';
import AdminSelect from '../../input/AdminSelect';
import XIcon from 'react-bootstrap-icons/dist/icons/x';

interface NewCriterionProps<Option> {
    options: Option[];
    record: Record<CriterionDisplayOption, Option>;
    onCreate: (
        name: string,
        displayOption: CriterionDisplayOption,
        mandatory: boolean,
    ) => void;
    onClose: () => void;
}

interface Values {
    name: string;
    displayOption: CriterionDisplayOption | null;
    mandatory: boolean;
}

const validationSchema = new ObjectSchema({
    name: new StringSchema().required().nonNullable(),
    displayOption: new NumberSchema().required().nonNullable(),
    mandatory: new BooleanSchema(),
});

export default function NewCriterion<Option>(props: NewCriterionProps<Option>) {
    const { options, record, onCreate, onClose } = props;
    const [values, setValues] = useState<Values>({
        name: '',
        displayOption: null,
        mandatory: false,
    });
    const translate = useTranslate();
    const debounce = useDebounce();

    return (
        <div className={styles.wrapper}>
            <AdminInput
                value={values.name}
                onChange={handleChange(values)}
                placeholder={translate('Nazev')}
            />
            <AdminSelect<Option>
                options={options}
                value={
                    values.displayOption
                        ? record[values.displayOption]
                        : undefined
                }
                placeholder={translate('Zobrazeni')}
                onChange={handleSelect(values)}
            />
            <Checkbox
                label={translate('Povinne?')}
                checked={values.mandatory}
                onCheck={handleCheck(values)}
            />
            <XIcon size={20} className={styles.close} onClick={onClose} />
        </div>
    );

    function handleChange(values: Values) {
        return async (event: ChangeEvent<HTMLInputElement>) => {
            const name = event.target.value;
            const newValues = { ...values, name };
            setValues(newValues);
            await debounce(create, newValues);
        };
    }

    function handleCheck(values: Values) {
        return async (checked: boolean) => {
            const newValues = { ...values, mandatory: checked };
            setValues(newValues);
            await debounce(create, newValues);
        };
    }

    function handleSelect(values: Values) {
        return async (displayOption: CriterionDisplayOption) => {
            const newValues = { ...values, displayOption };
            setValues(newValues);
            await debounce(create, newValues);
        };
    }

    async function create(values: Values) {
        if (await validate(values)) {
            onCreate(
                values.name,
                values.displayOption as CriterionDisplayOption,
                values.mandatory,
            );
        }
    }

    async function validate(values: Values) {
        return await validationSchema.validate(values);
    }
}
